import React, {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useSelector} from "react-redux";
import moment from "moment";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFCard from "@/components/Card";
import VLFDatePicker from "@/components/Input/DatePicker";
import VLFSelectNew from "@/components/Input/SelectNew";
import VLFRadio from "@/components/Input/Radio";

import {PlayerAPI} from "@/api/player";

import ArrowBack from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";

import {
	checkRequiredFields,
	getTextField,
	getTextAreaField,
} from "@/utils/functions";
import {
	DEFAULT_CATEGORY_ID,
	SUCCESSFUL_OPERATION,
	ERROR_OPERATION,
	playerRoles,
	prospectPlayerStatus,
	sideOptions,
} from "@/utils/constants";

import "./index.scss";
import {useTranslation} from "react-i18next";

const HandleScoutingPlayerPage = (props) => {
	const {edit, handleNotification} = props;

	const navigate = useNavigate();
	const {id} = useParams();
	const seasonId = useSelector((state) => state?.season?.id);
	const {t} = useTranslation();

	const [loading, setLoading] = useState(false);
	const [errors, setErrors] = useState([]);
	const [fields, setFields] = useState({});

	const getPlayer = async () => {
		setLoading(true);
		try {
			const response = await PlayerAPI.getById(id, {seasonId});
			response.dateOfBirth =
				moment(response.dateOfBirth)?.year() > 1900
					? response.dateOfBirth
					: null;

			if (response.prospectStatus) {
				response.prospectPlayerStatus = {
					value: response.prospectStatus,
					label: prospectPlayerStatus(t)?.filter(
						(s) => s.value === response.prospectStatus,
					)?.[0]?.label,
				};
			}

			response.playerPrimaryRole = {
				value: response.goalkeeper ? t("goalkeeper") : response.primaryRole,
				label: response.goalkeeper ? t("goalkeeper") : response.primaryRole,
			};

			setFields(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (edit) {
			getPlayer();
		}
	}, [edit]);

	const getRequiredFields = () => {
		return ["lastName", "playerPrimaryRole"];
	};

	const savePlayer = async () => {
		const errors = checkRequiredFields(getRequiredFields(), fields);
		setErrors(errors);

		if (!errors.length) {
			let body = Object.assign({}, fields);
			body = {
				...fields,
				playerCategoryId: DEFAULT_CATEGORY_ID,
				firstName: fields.firstName ? fields.firstName : "",
				prospectStatus: fields?.prospectPlayerStatus?.value,
				goalkeeper: fields?.playerPrimaryRole?.value === "Portiere",
				primaryRole: fields?.playerPrimaryRole?.value,
				prospect: true,
				dateOfBirth: fields.dateOfBirth
					? moment(fields.dateOfBirth).format("YYYY-MM-DD")
					: undefined,
			};

			setLoading(true);

			try {
				edit ? await PlayerAPI.update(id, body) : await PlayerAPI.create(body);
				navigate("/scouting/players");
				handleNotification(SUCCESSFUL_OPERATION, "success");
			} catch (error) {
				handleNotification(ERROR_OPERATION, "error");
				setLoading(false);
			}
		}
	};

	const handleSubmit = (event) => {
		event.preventDefault();
		savePlayer();
	};

	const goBack = () => {
		navigate("/scouting/players");
	};

	const mainActions = [
		{icon: <ArrowBack />, title: "Indietro", onClick: goBack, color: "info"},
		{
			icon: <SaveIcon />,
			title: "Salva",
			onClick: handleSubmit,
			disabled: checkRequiredFields(getRequiredFields(), fields).length
				? true
				: false,
		},
	];

	const defaultTextField = {
		values: fields,
		setValues: setFields,
		errors,
		setErrors,
	};

	const playerLabel =
		fields?.firstName || fields?.lastName
			? (fields.firstName || "") + " " + (fields.lastName || "")
			: "";

	const breadcrumbsElements = [{label: "Giocatori", path: "/scouting/players"}];
	if (edit) {
		breadcrumbsElements.push(
			{label: "/"},
			{
				label: playerLabel,
				path: "/scouting/players/" + id,
			},
		);
	}

	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				actions={<MainActions actions={mainActions} />}
			/>
			<div className="sheet-container">
				<div className="row no-margin">
					<div className="col-sm-12 padding-small">
						<VLFCard
							title="Informazioni principali"
							content={
								<div className="row no-margin">
									<div className="col-12 col-md-6 padding-small">
										{getTextField({
											...defaultTextField,
											fieldKey: "firstName",
											label: t("name"),
										})}
									</div>
									<div className="col-12 col-md-6 padding-small">
										{getTextField({
											...defaultTextField,
											fieldKey: "lastName",
											label: "Cognome",
											required: true,
										})}
									</div>
									<div className="col-12 col-md-6 padding-small">
										<VLFDatePicker
											label="Data di nascita"
											value={fields.dateOfBirth}
											onChange={(value) => {
												setFields({...fields, dateOfBirth: value});
											}}
										/>
									</div>
									<div className="col-12 col-md-6 padding-small">
										{getTextField({
											...defaultTextField,
											type: "number",
											fieldKey: "height",
											label: "Altezza (cm)",
										})}
									</div>
									<div className="col-12 col-md-6 padding-small">
										{getTextField({
											...defaultTextField,
											type: "number",
											fieldKey: "weight",
											label: "Peso (kg)",
										})}
									</div>
									{/* <div className="col-12 col-md-6 padding-small">
										<VLFSelectNew
											options={prospectPlayerStatus}
											sorted={false}
											value={fields.prospectPlayerStatus}
											onChange={(newValue) => {
												setFields({...fields, prospectPlayerStatus: newValue});
											}}
											label="Status"
										/>
									</div> */}
									<div className="col-12 col-md-6 padding-small">
										{getTextField({
											...defaultTextField,
											fieldKey: "favoriteFoot",
											label: "Piede principale",
										})}
									</div>
									<div className="col-12 col-md-6 padding-small">
										{getTextField({
											...defaultTextField,
											type: "number",
											fieldKey: "fatherHeight",
											label: "Altezza padre (cm)",
										})}
									</div>
									<div className="col-12 col-md-6 padding-small">
										{getTextField({
											...defaultTextField,
											type: "number",
											fieldKey: "motherHeight",
											label: "Altezza madre (cm)",
										})}
									</div>
									<div className="col-12 padding-small">
										{getTextAreaField({
											...defaultTextField,
											fieldKey: "notes",
											label: "Note",
											minRows: 3,
											maxRows: 3,
										})}
									</div>
								</div>
							}
						/>
					</div>
				</div>
				<div className="row no-margin mt-2">
					<div className="col-sm-12 padding-small">
						<VLFCard
							title="Informazioni ruolo"
							content={
								<div className="row no-margin">
									<div className="col-sm-12 col-md-6">
										<VLFSelectNew
											options={playerRoles}
											sortOrder="descend"
											value={fields.playerPrimaryRole}
											onChange={(newValue) => {
												setFields({
													...fields,
													playerPrimaryRole: newValue,
												});
											}}
											label="Ruolo"
											required
										/>
									</div>
									{fields?.playerPrimaryRole?.value === "Portiere" ? (
										<div className="col-sm-12 col-md-6">
											<VLFRadio
												name="Mano"
												value={fields.favoriteHand || ""}
												onChange={(event) => {
													setFields({
														...fields,
														favoriteHand: event.target.value,
													});
												}}
												options={sideOptions(t)}
												label="Mano preferita"
												size="small"
											/>
										</div>
									) : null}
								</div>
							}
						/>
					</div>
				</div>
			</div>
		</Page>
	);
};

export default HandleScoutingPlayerPage;
